// sections/@dashboard/finance/RulesEnginePanel.tsx
import { useEffect, useState } from "react";
import {
  Stack,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  IconButton,
  Box,
  Tooltip,
  Switch,
} from "@mui/material";
import { useSnackbar } from "notistack";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import {
  ITransactionCategory,
  ITransactionRule,
} from "../../../@types/finance";
import { FinanceService } from "../../../api/finance.service";

const MATCH_FIELD_LABEL: Record<string, string> = {
  narrative: "narrative",
  counterparty: "counterparty",
  amount_range: "amount",
  transaction_type: "tx type",
};

interface Props {
  categories: ITransactionCategory[];
  onAddRule: () => void;
  onRefresh: () => void;
  refreshKey?: number;
}

export default function RulesEnginePanel({
  categories,
  onAddRule,
  onRefresh,
  refreshKey,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState<ITransactionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const loadRules = async () => {
    try {
      setLoading(true);
      const financeService = new FinanceService();
      const res = await financeService.getRules();
      setRules(
        res.data.sort(
          (a: { priority: number; id: number }, b: { priority: number; id: number }) =>
            a.priority - b.priority || a.id - b.id,
        ),
      );
    } catch {
      enqueueSnackbar("Failed to load rules", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (rule: ITransactionRule) => {
    // Optimistically flip the switch, then reconcile with the server response
    // so the UI never shows "success" while the underlying status is stale.
    const nextActive = !rule.isActive;
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, isActive: nextActive } : r)),
    );
    try {
      const financeService = new FinanceService();
      await financeService.updateRuleStatus(rule.id.toString(), nextActive);
      enqueueSnackbar(nextActive ? "Rule activated!" : "Rule deactivated!");
    } catch {
      enqueueSnackbar("Failed to update rule", { variant: "error" });
    } finally {
      loadRules();
    }
  };

  // Move a rule up/down in priority order. Renumbers every rule's priority
  // to a clean multiple of 10 in the new order, so ties (e.g. rules created
  // with the same default priority) can never block a reorder from sticking.
  const handleMove = async (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= rules.length || reordering) return;

    const reordered = [...rules];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];
    setRules(reordered);
    setReordering(true);
    try {
      const financeService = new FinanceService();
      await Promise.all(
        reordered.map((rule, i) =>
          financeService.updateRulePriority(rule.id.toString(), (i + 1) * 10),
        ),
      );
      enqueueSnackbar("Rule order updated!");
    } catch {
      enqueueSnackbar("Failed to reorder rules", { variant: "error" });
    } finally {
      setReordering(false);
      loadRules();
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const financeService = new FinanceService();
      await financeService.deleteRule(id);
      enqueueSnackbar("Rule removed!");
      loadRules();
    } catch {
      enqueueSnackbar("Failed to delete rule", { variant: "error" });
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          Rules run in priority order. Lower number fires first. First match
          wins.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={onAddRule}>
          Add Rule
        </Button>
      </Stack>

      <Scrollbar>
        <Table size="small" sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              <TableCell width={90}>Priority</TableCell>
              <TableCell>Match field</TableCell>
              <TableCell>Operator</TableCell>
              <TableCell>Match value</TableCell>
              <TableCell>Assigns to</TableCell>
              <TableCell align="center">VAT</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="center">Matches</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, index) => {
              const cat = categories.find((c) => c.id === rule.categoryId);
              const catColor =
                cat?.type === "income"
                  ? "success"
                  : cat?.type === "expenditure"
                    ? "error"
                    : "secondary";

              return (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "grey.100",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "monospace",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "text.secondary",
                        }}>
                        {rule.priority}
                      </Box>
                      <Stack>
                        <IconButton
                          size="small"
                          sx={{ p: 0 }}
                          disabled={index === 0 || reordering}
                          onClick={() => handleMove(index, "up")}>
                          <Iconify icon="eva:arrow-up-fill" width={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ p: 0 }}
                          disabled={index === rules.length - 1 || reordering}
                          onClick={() => handleMove(index, "down")}>
                          <Iconify icon="eva:arrow-down-fill" width={14} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {MATCH_FIELD_LABEL[rule.matchField] ?? rule.matchField}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {rule.matchOperator}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      component="code"
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.75,
                        bgcolor: "grey.100",
                        fontSize: 12,
                        fontFamily: "monospace",
                        color: "text.primary",
                      }}>
                      {rule.matchValue}
                      {rule.matchValue2 && ` — ${rule.matchValue2}`}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {cat ? (
                      <Chip
                        label={cat.name}
                        size="small"
                        color={catColor as any}
                        variant="soft"
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {cat?.vatCode ? (
                      <Typography
                        variant="caption"
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.75,
                          bgcolor:
                            cat.vatCode === "S"
                              ? "#dbeafe"
                              : cat.vatCode === "E"
                                ? "#dcfce7"
                                : cat.vatCode === "Z"
                                  ? "#f3f4f6"
                                  : "#fef9c3",
                          color:
                            cat.vatCode === "S"
                              ? "#1d4ed8"
                              : cat.vatCode === "E"
                                ? "#15803d"
                                : cat.vatCode === "Z"
                                  ? "#374151"
                                  : "#854d0e",
                          fontWeight: 700,
                          fontFamily: "monospace",
                        }}>
                        {cat.vatCode}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {rule.branch?.name ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {rule.contactName ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" fontFamily="monospace">
                      {rule.matchCount ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={rule.isActive}
                      onChange={() => handleToggleActive(rule)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete rule">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(rule.id.toString())}>
                        <Iconify icon="eva:trash-2-fill" width={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Scrollbar>
    </Stack>
  );
}
